import React, { useCallback, useMemo } from "react";

import qs from "query-string";

import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import useToggle from "react-use/lib/useToggle";
import { useTranslation } from "react-i18next";

import { useMutation, useQuery } from "@apollo/client";

import Table from "../../../../components/Table";
import { useClientId } from "../hooks/useClient";
import { getApiUrl } from "../../../../lib/common/getApiUrl";
import { MUTATION_DELETE_GOOGLE_WORKSPACE_INTEGRATION } from "../../../../config/graphql/mutation";
import { QUERY_GOOGLE_WORKSPACES } from "../../../../config/graphql/query";

const TableRow = ({ item }: { item: IGoogleWorkspace }) => {
  const { t } = useTranslation(["client", "common"]);

  const { id, name, email, client } = item;

  const [visible, setVisible] = useToggle(false);

  const [onDelete] = useMutation(MUTATION_DELETE_GOOGLE_WORKSPACE_INTEGRATION, {
    refetchQueries: [
      {
        query: QUERY_GOOGLE_WORKSPACES,
        variables: { id: client?.id },
      },
    ],
    variables: { id },
  });

  const onBeforeDelete = () => {
    onDelete();
    return setVisible(false);
  };

  return (
    <>
      <tr>
        <td>{id}</td>
        <td>{name}</td>
        <td>{email}</td>
        <td>
          <div className="d-flex">
            <Button
              size="sm"
              variant="danger"
              className="mr-2"
              onClick={() => setVisible(true)}
            >
              {t("common:delete")}
            </Button>
          </div>
        </td>
      </tr>
      <Modal
        size="sm"
        show={visible}
        onHide={setVisible}
        centered
        aria-labelledby="contained-modal-title-vcenter"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t("client:client.integrations.googleWorkspace.modal.title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("client:client.integrations.googleWorkspace.modal.body", { id })}
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => setVisible(false)}>
            {t("common:cancel")}
          </Button>
          <Button size="sm" variant="danger" onClick={onBeforeDelete}>
            {t("common:delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const GoogleWorkspaceIntegrations = React.memo(() => {
  const clientId = useClientId();

  const { t } = useTranslation(["client", "common"]);

  const { data } = useQuery<{ googleWorkspaces: IGoogleWorkspace[] }>(
    QUERY_GOOGLE_WORKSPACES,
    {
      fetchPolicy: "network-only",
      context: {
        headers: { ClientId: clientId },
      },
    },
  );

  const items = data?.googleWorkspaces ?? [];

  const renderItem = useCallback(
    (item: IGoogleWorkspace) => <TableRow key={item.id} item={item} />,
    [],
  );

  const apiUrl = getApiUrl();

  const integrationOAuthURL = useMemo(
    () =>
      qs.stringifyUrl(
        {
          url: `${apiUrl}/api/v1/googleworkspace/auth/login`,
          query: {
            client: clientId,
            redirectUri: window.location.href,
          },
        },
        { encode: true },
      ),
    [clientId, apiUrl],
  );

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-end align-items-center my-4">
        <a href={integrationOAuthURL} className="btn btn-primary">
          {t("client:client.integrations.googleWorkspace.button.generate")}
        </a>
      </div>
      <>
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th scope="col">
                {t("client:client.integrations.googleWorkspace.th.id")}
              </th>
              <th scope="col">
                {t("client:client.integrations.googleWorkspace.th.name")}
              </th>
              <th scope="col">
                {t("client:client.integrations.googleWorkspace.th.email")}
              </th>
              <th scope="col">
                {t("client:client.integrations.googleWorkspace.th.actions")}
              </th>
            </tr>
          </thead>
          <tbody>{items.map(renderItem)}</tbody>
        </Table>
      </>
    </div>
  );
});

export default GoogleWorkspaceIntegrations;
